<template>
  <div class="space-y-1 px-6 py-4">
    <!-- Error message -->
    <p
      v-if="errorStatus"
      class="text-xs text-red-400 text-center py-1">
      Something went wrong, try again
    </p>

    <!-- NO STATUS: Show state flow options -->
    <template v-if="!currentStatus">
      <p
        class="px-4 pb-2 text-xs font-medium tracking-wide text-gray-400 uppercase">
        Track this title
      </p>
      <div class="flex gap-1 items-center px-2">
        <!-- Watchlist -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-amber-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'watchlist' ? 'bg-amber-500/20' : ''"
          @click="selectStatus('watchlist')">
          <fa-icon
            v-if="loadingStatus === 'watchlist'"
            icon="spinner-third"
            class="fa-spin text-xl text-amber-400" />
          <fa-icon
            v-else
            :icon="['far', 'bookmark']"
            class="text-xl text-amber-400" />
          <span class="text-xs font-medium">Watchlist</span>
        </button>

        <!-- Watching -->
        <button
          v-if="usesLogSystem"
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-cyan-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'in_progress' ? 'bg-cyan-500/20' : ''"
          @click="selectStatus('in_progress')">
          <fa-icon
            v-if="loadingStatus === 'in_progress'"
            icon="spinner-third"
            class="fa-spin text-xl text-cyan-400" />
          <fa-icon
            v-else
            :icon="['far', 'play-circle']"
            class="text-xl text-cyan-400" />
          <span class="text-xs font-medium">Watching</span>
        </button>

        <!-- Seen -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-green-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'seen' ? 'bg-green-500/20' : ''"
          @click="selectStatus('seen')">
          <fa-icon
            v-if="loadingStatus === 'seen'"
            icon="spinner-third"
            class="fa-spin text-xl text-green-400" />
          <fa-icon
            v-else
            :icon="['far', 'hexagon-check']"
            class="text-xl text-green-400" />
          <span class="text-xs font-medium">Seen</span>
        </button>
      </div>

      <!-- Did not finish option -->
      <div class="mt-3 border-t border-white/10">
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-red-400 rounded-lg transition-colors cursor-pointer hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-default"
          @click="selectStatus('dropped')">
          <fa-icon
            v-if="loadingStatus === 'dropped'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-stop']" class="w-5" />
          <span class="font-medium">Did not finish</span>
        </button>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 mt-2 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="['far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          v-if="standaloneNote"
          :icon="['fas', 'check']"
          class="text-blue-400" />
      </button>
    </template>

    <!-- WATCHLIST STATUS -->
    <template v-else-if="currentStatus === 'watchlist'">
      <!-- Current state indicator with metadata -->
      <div class="px-4 py-3 mb-2 rounded-lg bg-amber-500/20">
        <div class="flex gap-3 items-center text-amber-400">
          <fa-icon :icon="['fas', 'bookmark']" class="w-5" />
          <div class="flex-1">
            <span class="font-medium">In Watchlist</span>
            <div
              v-if="logMetadata?.service"
              class="flex flex-wrap gap-2 mt-1 text-xs text-amber-400/70">
              <span class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'tv']" class="text-2xs" />
                {{ logMetadata.service }}
              </span>
            </div>
            <p
              v-if="standaloneNote"
              class="mt-1 text-xs truncate text-amber-400/60">
              {{ standaloneNote }}
            </p>
          </div>
          <fa-icon :icon="['fas', 'check']" />
        </div>
      </div>

      <!-- Progression options -->
      <p
        class="px-4 pt-2 pb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">
        Progress to
      </p>
      <div class="flex gap-1 items-center px-2">
        <!-- Current: Watchlist (highlighted) -->
        <div
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg bg-amber-500/20">
          <fa-icon
            :icon="['fas', 'bookmark']"
            class="text-xl text-amber-400" />
          <span class="text-xs font-medium text-amber-400">Watchlist</span>
        </div>

        <button
          v-if="usesLogSystem"
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-cyan-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'in_progress' ? 'bg-cyan-500/20' : ''"
          @click="selectStatus('in_progress')">
          <fa-icon
            v-if="loadingStatus === 'in_progress'"
            icon="spinner-third"
            class="fa-spin text-xl text-cyan-400" />
          <fa-icon
            v-else
            :icon="['far', 'play-circle']"
            class="text-xl text-cyan-400" />
          <span class="text-xs font-medium">Watching</span>
        </button>

        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-green-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'seen' ? 'bg-green-500/20' : ''"
          @click="selectStatus('seen')">
          <fa-icon
            v-if="loadingStatus === 'seen'"
            icon="spinner-third"
            class="fa-spin text-xl text-green-400" />
          <fa-icon
            v-else
            :icon="['far', 'hexagon-check']"
            class="text-xl text-green-400" />
          <span class="text-xs font-medium">Seen</span>
        </button>
      </div>

      <div class="mt-3 border-t border-white/10">
        <!-- Did not finish option -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-red-400 rounded-lg transition-colors cursor-pointer hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-default"
          @click="selectStatus('dropped')">
          <fa-icon
            v-if="loadingStatus === 'dropped'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-stop']" class="w-5" />
          <span class="font-medium">Did not finish</span>
        </button>

        <!-- Remove option -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-gray-400 rounded-lg transition-colors cursor-pointer hover:bg-white/10 disabled:opacity-60 disabled:cursor-default"
          @click="clearWatchlistIntent">
          <fa-icon
            v-if="loadingStatus === 'clearing'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-xmark']" class="w-5" />
          <span class="font-medium">Remove from Watchlist</span>
        </button>
      </div>

      <!-- Multiple states warning -->
      <div
        v-if="hasMultipleStates"
        class="px-4 py-3 mt-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
        <p class="text-xs text-amber-400 mb-2">
          <fa-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
          Also tracked as: {{ allActiveStates.filter(s => s !== 'watchlist').map(getStateName).join(', ') }}
        </p>
        <button
          type="button"
          class="text-xs text-amber-400 hover:text-amber-300 font-medium"
          @click="clearAllStatuses">
          Clear all statuses
        </button>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="['far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          v-if="standaloneNote"
          :icon="['fas', 'check']"
          class="text-blue-400" />
      </button>
    </template>

    <!-- IN PROGRESS STATUS -->
    <template v-else-if="currentStatus === 'in_progress'">
      <div class="px-4 py-3 mb-2 rounded-lg bg-cyan-500/20">
        <div class="flex gap-3 items-center text-cyan-400">
          <fa-icon :icon="['fas', 'play-circle']" class="w-5" />
          <div class="flex-1">
            <span class="font-medium">Currently Watching</span>
            <div
              v-if="logMetadata?.service || logMetadata?.watchedOn"
              class="flex flex-wrap gap-2 mt-1 text-xs text-cyan-400/70">
              <span
                v-if="logMetadata?.watchedOn"
                class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'calendar']" class="text-2xs" />
                Started {{ formatMetadataDate(logMetadata.watchedOn) }}
              </span>
              <span
                v-if="logMetadata?.service"
                class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'tv']" class="text-2xs" />
                {{ logMetadata.service }}
              </span>
            </div>
            <p
              v-if="standaloneNote"
              class="mt-1 text-xs truncate text-cyan-400/60">
              {{ standaloneNote }}
            </p>
          </div>
          <fa-icon :icon="['fas', 'check']" />
        </div>
      </div>

      <p
        class="px-4 pt-2 pb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">
        Progress
      </p>
      <div class="flex gap-1 items-center px-2">
        <!-- Watchlist - independent intent toggle (doc 27), coexists with
             the history state -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg transition-colors cursor-pointer hover:bg-amber-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'watchlist' || isWatchlisted
              ? 'bg-amber-500/20 text-white'
              : 'text-white/50'
          "
          @click="toggleWatchlistIntent">
          <fa-icon
            v-if="loadingStatus === 'watchlist'"
            icon="spinner-third"
            class="fa-spin text-xl text-amber-400" />
          <fa-icon
            v-else
            :icon="isWatchlisted ? ['fas', 'bookmark'] : ['far', 'bookmark']"
            class="text-xl"
            :class="isWatchlisted ? 'text-amber-400' : 'text-amber-400/50'" />
          <span class="text-xs font-medium">Watchlist</span>
        </button>

        <div
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg bg-cyan-500/20">
          <fa-icon
            :icon="['fas', 'play-circle']"
            class="text-xl text-cyan-400" />
          <span class="text-xs font-medium text-cyan-400">Watching</span>
        </div>

        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white rounded-lg transition-colors cursor-pointer hover:bg-green-500/20 disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'seen' ? 'bg-green-500/20' : ''"
          @click="selectStatus('seen')">
          <fa-icon
            v-if="loadingStatus === 'seen'"
            icon="spinner-third"
            class="fa-spin text-xl text-green-400" />
          <fa-icon
            v-else
            :icon="['far', 'hexagon-check']"
            class="text-xl text-green-400" />
          <span class="text-xs font-medium">Seen</span>
        </button>
      </div>

      <div class="mt-3 border-t border-white/10">
        <!-- Did not finish option -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-red-400 rounded-lg transition-colors cursor-pointer hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-default"
          @click="selectStatus('dropped')">
          <fa-icon
            v-if="loadingStatus === 'dropped'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-stop']" class="w-5" />
          <span class="font-medium">Did not finish</span>
        </button>

        <!-- Remove from Watching -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-gray-400 rounded-lg transition-colors cursor-pointer hover:bg-white/10 disabled:opacity-60 disabled:cursor-default"
          @click="clearHistory">
          <fa-icon
            v-if="loadingStatus === 'clearing'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-xmark']" class="w-5" />
          <span class="font-medium">Remove from Watching</span>
        </button>
      </div>

      <!-- Multiple states warning -->
      <div
        v-if="hasMultipleStates"
        class="px-4 py-3 mt-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
        <p class="text-xs text-amber-400 mb-2">
          <fa-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
          Also tracked as: {{ allActiveStates.filter(s => s !== 'in_progress' && s !== 'watchlist').map(getStateName).join(', ') }}
        </p>
        <button
          type="button"
          class="text-xs text-amber-400 hover:text-amber-300 font-medium"
          @click="clearAllStatuses">
          Clear all statuses
        </button>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="['far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          v-if="standaloneNote"
          :icon="['fas', 'check']"
          class="text-blue-400" />
      </button>
    </template>

    <!-- SEEN STATUS -->
    <template v-else-if="currentStatus === 'seen'">
      <div class="px-4 py-3 mb-2 rounded-lg bg-green-500/20">
        <div class="flex gap-3 items-center text-green-400">
          <fa-icon :icon="['fas', 'hexagon-check']" class="w-5" />
          <div class="flex-1">
            <span class="font-medium">Seen</span>
            <div
              v-if="logMetadata?.service || logMetadata?.watchedOn"
              class="flex flex-wrap gap-2 mt-1 text-xs text-green-400/70">
              <span
                v-if="logMetadata?.watchedOn"
                class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'calendar']" class="text-2xs" />
                {{ formatMetadataDate(logMetadata.watchedOn) }}
              </span>
              <span
                v-if="logMetadata?.service"
                class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'tv']" class="text-2xs" />
                {{ logMetadata.service }}
              </span>
            </div>
            <p
              v-if="standaloneNote"
              class="mt-1 text-xs truncate text-green-400/60">
              {{ standaloneNote }}
            </p>
          </div>
          <fa-icon :icon="['fas', 'check']" />
        </div>
      </div>

      <!-- Rewatch option -->
      <button
        v-if="usesLogSystem"
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-purple-500/20"
        @click="handleRewatch">
        <fa-icon
          :icon="['far', 'rotate-right']"
          class="w-5 text-purple-400" />
        <span class="flex-1 font-medium">Log a Rewatch</span>
      </button>

      <p
        class="px-4 pt-3 pb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">
        Change status
      </p>
      <div class="flex gap-1 items-center px-2">
        <!-- Watchlist - independent intent toggle (doc 27), coexists with
             the history state -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg transition-colors cursor-pointer hover:bg-amber-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'watchlist' || isWatchlisted
              ? 'bg-amber-500/20 text-white'
              : 'text-white/50'
          "
          @click="toggleWatchlistIntent">
          <fa-icon
            v-if="loadingStatus === 'watchlist'"
            icon="spinner-third"
            class="fa-spin text-xl text-amber-400" />
          <fa-icon
            v-else
            :icon="isWatchlisted ? ['fas', 'bookmark'] : ['far', 'bookmark']"
            class="text-xl"
            :class="isWatchlisted ? 'text-amber-400' : 'text-amber-400/50'" />
          <span class="text-xs font-medium">Watchlist</span>
        </button>

        <fa-icon
          v-if="usesLogSystem"
          :icon="['fas', 'chevron-right']"
          class="text-xs text-gray-500" />

        <button
          v-if="usesLogSystem"
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white/50 rounded-lg transition-colors cursor-pointer hover:bg-cyan-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'in_progress' ? 'bg-cyan-500/20' : ''"
          @click="selectStatus('in_progress')">
          <fa-icon
            v-if="loadingStatus === 'in_progress'"
            icon="spinner-third"
            class="fa-spin text-xl text-cyan-400" />
          <fa-icon
            v-else
            :icon="['far', 'play-circle']"
            class="text-xl text-cyan-400/50" />
          <span class="text-xs font-medium">Watching</span>
        </button>

        <fa-icon
          :icon="['fas', 'chevron-right']"
          class="text-xs text-gray-500" />

        <div
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg bg-green-500/20">
          <fa-icon
            :icon="['fas', 'hexagon-check']"
            class="text-xl text-green-400" />
          <span class="text-xs font-medium text-green-400">Seen</span>
        </div>
      </div>

      <div class="mt-3 border-t border-white/10">
        <!-- Did not finish option -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-red-400 rounded-lg transition-colors cursor-pointer hover:bg-red-500/20 disabled:opacity-60 disabled:cursor-default"
          @click="selectStatus('dropped')">
          <fa-icon
            v-if="loadingStatus === 'dropped'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-stop']" class="w-5" />
          <span class="font-medium">Did not finish</span>
        </button>

        <!-- Clear status (clears history state and watchlist intent) -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex gap-3 items-center px-4 py-3 w-full text-left text-gray-400 rounded-lg transition-colors cursor-pointer hover:bg-white/10 disabled:opacity-60 disabled:cursor-default"
          @click="clearAllStatuses">
          <fa-icon
            v-if="loadingStatus === 'clearing'"
            icon="spinner-third"
            class="fa-spin w-5" />
          <fa-icon v-else :icon="['far', 'circle-xmark']" class="w-5" />
          <span class="font-medium">Clear status</span>
        </button>
      </div>

      <!-- Multiple states warning -->
      <div
        v-if="hasMultipleStates"
        class="px-4 py-3 mt-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
        <p class="text-xs text-amber-400 mb-2">
          <fa-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
          Also tracked as: {{ allActiveStates.filter(s => s !== 'seen' && s !== 'watchlist').map(getStateName).join(', ') }}
        </p>
        <button
          type="button"
          class="text-xs text-amber-400 hover:text-amber-300 font-medium"
          @click="clearAllStatuses">
          Clear all statuses
        </button>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="['far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          v-if="standaloneNote"
          :icon="['fas', 'check']"
          class="text-blue-400" />
      </button>
    </template>

    <!-- DID NOT FINISH STATUS -->
    <template v-else-if="currentStatus === 'dropped'">
      <div class="px-4 py-3 mb-2 rounded-lg bg-red-500/20">
        <div class="flex gap-3 items-center text-red-400">
          <fa-icon :icon="['fas', 'circle-stop']" class="w-5" />
          <div class="flex-1">
            <span class="font-medium">Did not finish</span>
            <div
              v-if="logMetadata?.watchedOn"
              class="flex flex-wrap gap-2 mt-1 text-xs text-red-400/70">
              <span class="flex gap-1 items-center">
                <fa-icon :icon="['far', 'calendar']" class="text-2xs" />
                {{ formatMetadataDate(logMetadata.watchedOn) }}
              </span>
            </div>
            <p
              v-if="standaloneNote"
              class="mt-1 text-xs truncate text-red-400/60">
              {{ standaloneNote }}
            </p>
          </div>
          <fa-icon :icon="['fas', 'check']" />
        </div>
      </div>

      <p
        class="px-4 pt-2 pb-1 text-xs font-medium tracking-wide text-gray-400 uppercase">
        Change status
      </p>
      <div class="flex gap-1 items-center px-2">
        <!-- Watchlist - independent intent toggle (doc 27), coexists with
             the history state -->
        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg transition-colors cursor-pointer hover:bg-amber-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="
            loadingStatus === 'watchlist' || isWatchlisted
              ? 'bg-amber-500/20 text-white'
              : 'text-white/50'
          "
          @click="toggleWatchlistIntent">
          <fa-icon
            v-if="loadingStatus === 'watchlist'"
            icon="spinner-third"
            class="fa-spin text-xl text-amber-400" />
          <fa-icon
            v-else
            :icon="isWatchlisted ? ['fas', 'bookmark'] : ['far', 'bookmark']"
            class="text-xl"
            :class="isWatchlisted ? 'text-amber-400' : 'text-amber-400/50'" />
          <span class="text-xs font-medium">Watchlist</span>
        </button>

        <button
          v-if="usesLogSystem"
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white/50 rounded-lg transition-colors cursor-pointer hover:bg-cyan-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'in_progress' ? 'bg-cyan-500/20' : ''"
          @click="selectStatus('in_progress')">
          <fa-icon
            v-if="loadingStatus === 'in_progress'"
            icon="spinner-third"
            class="fa-spin text-xl text-cyan-400" />
          <fa-icon
            v-else
            :icon="['far', 'play-circle']"
            class="text-xl text-cyan-400/50" />
          <span class="text-xs font-medium">Watching</span>
        </button>

        <button
          type="button"
          :disabled="!!loadingStatus"
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center text-white/50 rounded-lg transition-colors cursor-pointer hover:bg-green-500/20 hover:text-white disabled:opacity-60 disabled:cursor-default"
          :class="loadingStatus === 'seen' ? 'bg-green-500/20' : ''"
          @click="selectStatus('seen')">
          <fa-icon
            v-if="loadingStatus === 'seen'"
            icon="spinner-third"
            class="fa-spin text-xl text-green-400" />
          <fa-icon
            v-else
            :icon="['far', 'hexagon-check']"
            class="text-xl text-green-400/50" />
          <span class="text-xs font-medium">Seen</span>
        </button>

        <div
          class="flex flex-col flex-1 gap-2 items-center px-2 py-3 text-center rounded-lg bg-red-500/20">
          <fa-icon
            :icon="['fas', 'circle-stop']"
            class="text-xl text-red-400" />
          <span class="text-xs font-medium text-red-400">DNF</span>
        </div>
      </div>

      <button
        type="button"
        :disabled="!!loadingStatus"
        class="flex gap-3 items-center px-4 py-3 mt-3 w-full text-left text-gray-400 rounded-lg border-t transition-colors cursor-pointer border-white/10 hover:bg-white/10 disabled:opacity-60 disabled:cursor-default"
        @click="clearAllStatuses">
        <fa-icon
          v-if="loadingStatus === 'clearing'"
          icon="spinner-third"
          class="fa-spin w-5" />
        <fa-icon v-else :icon="['far', 'circle-xmark']" class="w-5" />
        <span class="font-medium">Clear status</span>
      </button>

      <!-- Multiple states warning -->
      <div
        v-if="hasMultipleStates"
        class="px-4 py-3 mt-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
        <p class="text-xs text-amber-400 mb-2">
          <fa-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
          Also tracked as: {{ allActiveStates.filter(s => s !== 'dropped' && s !== 'watchlist').map(getStateName).join(', ') }}
        </p>
        <button
          type="button"
          class="text-xs text-amber-400 hover:text-amber-300 font-medium"
          @click="clearAllStatuses">
          Clear all statuses
        </button>
      </div>

      <!-- Add/Edit Note option -->
      <button
        type="button"
        class="flex gap-3 items-center px-4 py-3 w-full text-left text-white rounded-lg transition-colors cursor-pointer hover:bg-white/10"
        @click="openNoteSheet">
        <fa-icon :icon="['far', 'note-sticky']" class="w-5 text-blue-400" />
        <span class="flex-1 font-medium">{{
          standaloneNote ? 'Edit Note' : 'Add Note'
        }}</span>
        <fa-icon
          v-if="standaloneNote"
          :icon="['fas', 'check']"
          class="text-blue-400" />
      </button>
    </template>
  </div>

  <!-- Metadata Sheet -->
  <LogMetadataSheet
    v-model="showMetadataSheet"
    :status="pendingStatus === 'rewatch' ? 'seen' : pendingStatus || 'seen'"
    :is-rewatch="pendingStatus === 'rewatch'"
    :show-date-field="pendingStatus !== 'watchlist'"
    :current-log="currentLog"
    :title-type="titleType"
    :title-name="titleName"
    :title="title"
    @save="handleMetadataSave"
    @skip="handleMetadataSkip" />

  <!-- Standalone Note Sheet -->
  <StandaloneNoteSheet
    v-model="showNoteSheet"
    :title="title"
    :title-type="titleType"
    :initial-note="standaloneNote"
    @saved="handleNoteSaved" />
</template>

<script setup>
import { ref, computed } from 'vue'
import { LogMetadataSheet, StandaloneNoteSheet } from '@/components/Sheets'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreProfile } from '@/stores/storeProfile'
import { useHapticFeedback } from '@/composables/useHapticFeedback'

const props = defineProps({
  title: {
    type: Object,
    default: null
  },
  currentLog: {
    type: Object,
    default: null
  },
  titleType: {
    type: String,
    default: 'movie'
  },
  titleName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['statusChanged', 'close'])

const haptics = useHapticFeedback()
const storeTitleLogs = useStoreTitleLogs()
const storeTitlesUser = useStoreTitlesUser()
const storeLists = useStoreLists()
const storeProfile = useStoreProfile()

// Loading state for status buttons
const loadingStatus = ref(null)
const errorStatus = ref(null)

// Metadata sheet state
const showMetadataSheet = ref(false)
const pendingStatus = ref(null)

// Note sheet state
const showNoteSheet = ref(false)

// Get standalone note from titles_user
const standaloneNote = computed(() => {
  if (!props.title) return null
  const titleUserData = storeTitlesUser.getTitleByTitleId({
    id: props.title.id,
    type: props.title.media_type || props.titleType
  })
  return titleUserData?.notes || null
})

// Open note sheet
const openNoteSheet = () => {
  emit('close')
  haptics.light()
  setTimeout(() => {
    showNoteSheet.value = true
  }, 100)
}

// Handle note saved
const handleNoteSaved = (noteText) => {
  emit('statusChanged', { noteUpdated: true, note: noteText })
}

// Log system is always enabled
const usesLogSystem = computed(() => true)

// Watchlist intent (doc 27) - independent of the history state, with the
// legacy list system as fallback for items without logs
const isWatchlisted = computed(() => {
  if (!props.title) return props.currentLog?.state === 'watchlist'
  const tmdbType = props.title.media_type || props.titleType

  if (storeTitleLogs.isWatchlistedByTmdb(props.title.id, tmdbType)) {
    return true
  }

  const watchlist = storeLists.getSystemListByType('watchlist')
  if (!watchlist) return false
  return storeLists.items.some(
    (i) =>
      i.list_id === watchlist.id &&
      i.titles?.tmdb_id == props.title.id &&
      i.titles?.tmdb_type == tmdbType
  )
})

// Current status reads the history state first (doc 27 decision 6);
// watchlist stands in only when no history log exists
const currentStatus = computed(() => {
  if (props.title) {
    const historyState = storeTitleLogs.getHistoryStateByTmdb(
      props.title.id,
      props.title.media_type || props.titleType
    )
    if (historyState) return historyState
  }

  if (props.currentLog && props.currentLog.state !== 'watchlist') {
    return props.currentLog.state
  }

  if (isWatchlisted.value) return 'watchlist'

  return null
})

// Check if title has multiple different states (data issue)
const hasMultipleStates = computed(() => {
  if (!props.title) return false
  return storeTitleLogs.hasMultipleStatesByTmdb(
    props.title.id,
    props.title.media_type || props.titleType
  )
})

// Get all active states for display
const allActiveStates = computed(() => {
  if (!props.title) return []
  return storeTitleLogs.getAllActiveStatesByTmdb(
    props.title.id,
    props.title.media_type || props.titleType
  )
})

// Extract metadata from current log
const logMetadata = computed(() => {
  if (!props.currentLog) return null
  return {
    service: props.currentLog.watched_service || null,
    notes: props.currentLog.notes || null,
    watchedOn: props.currentLog.watched_on || null,
    tvSeason: props.currentLog.tv_season || null,
    tvEpisode: props.currentLog.tv_episode || null
  }
})

// Format date for display
const formatMetadataDate = (dateString) => {
  if (!dateString) return null
  try {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    })
  } catch (e) {
    return null
  }
}

// Show error briefly then clear
const flashError = (status) => {
  errorStatus.value = status
  haptics.error()
  setTimeout(() => {
    errorStatus.value = null
  }, 2000)
}

// Handle status selection
const selectStatus = async (status) => {
  if (loadingStatus.value) return
  haptics.light()

  // Same status = no-op
  if (status === currentStatus.value) return

  // Apply status with loading indicator
  loadingStatus.value = status
  try {
    await applyStatus(status)
    emit('statusChanged', { status })
  } catch {
    flashError(status)
  } finally {
    loadingStatus.value = null
  }
}

// Toggle the watchlist intent without touching the history state (doc 27)
const toggleWatchlistIntent = async () => {
  if (loadingStatus.value) return
  haptics.light()
  loadingStatus.value = 'watchlist'
  try {
    if (isWatchlisted.value) {
      await removeWatchlistIntent()
      emit('statusChanged', { watchlisted: false })
    } else {
      await applyStatus('watchlist')
      emit('statusChanged', { watchlisted: true })
    }
  } catch {
    flashError('watchlist')
  } finally {
    loadingStatus.value = null
  }
}

// Handle rewatch
const handleRewatch = () => {
  emit('close')
  haptics.light()
  pendingStatus.value = 'rewatch'
  showMetadataSheet.value = true
}

// Handle metadata save
const handleMetadataSave = async (metadata) => {
  if (!pendingStatus.value || !props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    if (pendingStatus.value === 'rewatch') {
      // Create new seen log (rewatch)
      await storeTitleLogs.createLog(titleData, 'seen', metadata)
    } else {
      await applyStatus(pendingStatus.value, metadata)
    }

    emit('statusChanged', {
      status: pendingStatus.value === 'rewatch' ? 'seen' : pendingStatus.value,
      metadata
    })
  } catch (error) {
    console.error('Failed to apply status:', error)
  }

  pendingStatus.value = null
}

// Handle metadata skip
const handleMetadataSkip = async () => {
  if (!pendingStatus.value || !props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    if (pendingStatus.value === 'rewatch') {
      await storeTitleLogs.createLog(titleData, 'seen', {})
    } else {
      await applyStatus(pendingStatus.value)
    }

    emit('statusChanged', {
      status: pendingStatus.value === 'rewatch' ? 'seen' : pendingStatus.value
    })
  } catch (error) {
    console.error('Failed to apply status:', error)
  }

  pendingStatus.value = null
}

// Apply a status change
const applyStatus = async (status, metadata = {}) => {
  if (!props.title) return

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  switch (status) {
    case 'seen':
      await storeTitleLogs.markAsSeen(titleData, metadata)
      break
    case 'in_progress':
      await storeTitleLogs.startWatching(titleData, metadata)
      break
    case 'watchlist':
      await storeTitleLogs.addToWatchlist(titleData, metadata)
      break
    case 'dropped':
      await storeTitleLogs.markAsDropped(titleData, metadata)
      break
  }
}

// Run a clearing action with the shared loading/error handling
const runClearing = async (action) => {
  if (loadingStatus.value) return
  haptics.light()
  loadingStatus.value = 'clearing'
  try {
    await action()
    emit('statusChanged', { status: null })
  } catch {
    flashError('clearing')
  } finally {
    loadingStatus.value = null
  }
}

// Clear the active history state only - the watchlist intent survives
const clearHistory = () =>
  runClearing(async () => {
    if (!props.title) return

    const titleData = {
      id: props.title.id,
      type: props.title.media_type || props.titleType
    }

    const historyState = storeTitleLogs.getHistoryStateByTmdb(
      props.title.id,
      titleData.type
    )
    if (historyState) {
      await storeTitleLogs.removeFromState(titleData, historyState)
    }
  })

// Remove the watchlist intent only, leaving history logs alone
const removeWatchlistIntent = async () => {
  if (!props.title) return

  const tmdbType = props.title.media_type || props.titleType
  const titleData = { id: props.title.id, type: tmdbType }
  const hadIntentLog = storeTitleLogs.isWatchlistedByTmdb(
    props.title.id,
    tmdbType
  )

  await storeTitleLogs.removeFromState(titleData, 'watchlist')

  // If watchlist status came from list system fallback (no log entry),
  // remove directly from the list system
  if (!hadIntentLog) {
    const watchlist = storeLists.getSystemListByType('watchlist')
    if (watchlist) {
      const item = storeLists.items.find(
        (i) =>
          i.list_id === watchlist.id &&
          i.titles?.tmdb_id == props.title.id &&
          i.titles?.tmdb_type == tmdbType
      )
      if (item) {
        await storeLists.removeItemFromList(watchlist.id, item.id)
      }
    }
  }
}

const clearWatchlistIntent = () => runClearing(removeWatchlistIntent)

// Clear ALL statuses (for duplicate cleanup)
const clearAllStatuses = async () => {
  if (!props.title) return
  emit('close')
  haptics.medium()

  const titleData = {
    id: props.title.id,
    type: props.title.media_type || props.titleType
  }

  try {
    await storeTitleLogs.clearAllStatuses(titleData)
    emit('statusChanged', { status: null, clearedAll: true })
  } catch (error) {
    console.error('Failed to clear all statuses:', error)
  }
}

// Get readable state name
const getStateName = (state) => {
  switch (state) {
    case 'seen':
      return 'Seen'
    case 'in_progress':
      return 'Watching'
    case 'watchlist':
      return 'Watchlist'
    case 'dropped':
      return 'Did not finish'
    default:
      return state
  }
}
</script>
